import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useUser } from '../../context/UserContext';
import {
  listFeatureCards,
  addFeatureCard,
  deleteFeatureCard,
  sendEmails,
} from '../../services/newFeaturesService';
import './NewFeatures.css';

const TABS = [
  'All',
  'Ask GrowthIQ',
  'Competitor Intelligence',
  'Agents',
  'Markets',
  'Automation',
];

const TAB_BADGE_CLASS = {
  'Ask GrowthIQ': 'nf-badge-ask',
  'Competitor Intelligence': 'nf-badge-competitor',
  'Agents': 'nf-badge-agents',
  'Markets': 'nf-badge-markets',
  'Automation': 'nf-badge-automation',
};

const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const EMPTY_FORM = {
  feature_card_id: null,
  title: '',
  tab: 'Ask GrowthIQ',
  description: '',
  url: '',
  tags: '',
  released_date: '',
  image1: null,
  image2: null,
};

function formatDate(isoString) {
  if (!isoString) return '';
  const d = new Date(isoString);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

function TabBadge({ tab }) {
  const cls = TAB_BADGE_CLASS[tab] || 'nf-badge-default';
  return <span className={`nf-badge ${cls}`}>{tab}</span>;
}

function TagList({ tags }) {
  if (!tags || tags.length === 0) return null;
  const flat = tags.flatMap((t) => t.split(',').map((s) => s.trim())).filter(Boolean);
  const unique = [...new Set(flat)];
  return (
    <div className="nf-tags">
      {unique.map((tag, i) => (
        <span key={i} className="nf-tag">
          <span className="nf-tag-dot" />
          {tag}
        </span>
      ))}
    </div>
  );
}

function FeatureCard({ feature, isAdmin, onDelete, onSendEmail, onEdit }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState(false);
  const [emailSending, setEmailSending] = useState(false);

  const handleSendEmail = async () => {
    setEmailSending(true);
    setConfirmEmail(false);
    await onSendEmail(feature._id);
    setEmailSending(false);
  };

  const displayDate = feature.released_date || feature.created_at;

  return (
    <div className={`nf-card${feature.is_deleted ? ' nf-card-deleted' : ''}`}>
      <div className="nf-card-meta">
        <span className="nf-card-date">{formatDate(displayDate)}</span>
        {feature.tab && <TabBadge tab={feature.tab} />}
        {feature.is_deleted === 1 && (
          <span className="nf-badge" style={{ background: '#FEF2F2', borderColor: '#FECACA', color: '#EF4444' }}>
            Deleted
          </span>
        )}
        <TagList tags={feature.tags} />
      </div>

      <h3 className="nf-card-title">{feature.title}</h3>

      {feature.description && (
        <div
          className="nf-card-desc"
          dangerouslySetInnerHTML={{ __html: feature.description }}
        />
      )}

      {feature.images && feature.images.length > 0 && (
        <div className="nf-card-images">
          {feature.images.length === 1 ? (
            <img src={feature.images[0]} alt={feature.title} />
          ) : (
            <div
              id={`carousel-${feature._id}`}
              className="carousel slide"
              data-bs-ride="carousel"
            >
              <div className="carousel-indicators">
                {feature.images.map((_, idx) => (
                  <button
                    key={idx}
                    type="button"
                    data-bs-target={`#carousel-${feature._id}`}
                    data-bs-slide-to={idx}
                    className={idx === 0 ? 'active' : ''}
                  />
                ))}
              </div>
              <div className="carousel-inner">
                {feature.images.map((img, idx) => (
                  <div key={idx} className={`carousel-item${idx === 0 ? ' active' : ''}`}>
                    <img src={img} className="d-block w-100" alt={`slide-${idx}`} />
                  </div>
                ))}
              </div>
              <button
                className="carousel-control-prev"
                type="button"
                data-bs-target={`#carousel-${feature._id}`}
                data-bs-slide="prev"
              >
                <span className="carousel-control-prev-icon" />
              </button>
              <button
                className="carousel-control-next"
                type="button"
                data-bs-target={`#carousel-${feature._id}`}
                data-bs-slide="next"
              >
                <span className="carousel-control-next-icon" />
              </button>
            </div>
          )}
        </div>
      )}

      {feature.url && (
        <div>
          <a
            href={feature.url}
            target="_blank"
            rel="noopener noreferrer"
            className="nf-know-more-btn"
          >
            Know More →
          </a>
        </div>
      )}

      {isAdmin && (
        <div className="nf-card-actions">
          <button className="nf-btn-edit" onClick={() => onEdit(feature)}>
            Edit
          </button>

          {feature.is_deleted === 1 ? (
            <span className="nf-btn-status-deleted">Deleted</span>
          ) : !confirmDelete ? (
            <button className="nf-btn-delete" onClick={() => setConfirmDelete(true)}>
              Delete
            </button>
          ) : (
            <>
              <button className="nf-btn-delete" onClick={() => onDelete(feature._id)}>
                Confirm Delete
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setConfirmDelete(false)}
              >
                Cancel
              </button>
            </>
          )}

          {feature.email_sent === 1 ? (
            <span className="nf-btn-status-email">Emails Sent</span>
          ) : !confirmEmail ? (
            <button className="nf-btn-email" onClick={() => setConfirmEmail(true)} disabled={emailSending}>
              {emailSending ? 'Sending…' : 'Send Email'}
            </button>
          ) : (
            <>
              <button className="nf-btn-email" onClick={handleSendEmail}>
                Confirm Send
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setConfirmEmail(false)}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function NewFeatures() {
  const { user } = useUser();
  const isAdmin = user?.growth_iq_role == 24;

  const [features, setFeatures] = useState([]);
  const [activeTab, setActiveTab] = useState('All');
  const [showDeleted, setShowDeleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [preview1, setPreview1] = useState(null);
  const [preview2, setPreview2] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const isEditMode = Boolean(form.feature_card_id);

  const fetchFeatures = useCallback(async (tab, deleted) => {
    setLoading(true);
    try {
      const res = await listFeatureCards(tab, deleted ? 1 : 0);
      setFeatures(res.data?.data || []);
    } catch {
      setFeatures([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeatures(activeTab, showDeleted);
  }, [activeTab, showDeleted, fetchFeatures]);

  const openModal = (existingFeature = null) => {
    if (existingFeature) {
      const tagStr = (existingFeature.tags || [])
        .flatMap((t) => t.split(',').map((s) => s.trim()))
        .filter(Boolean)
        .join(', ');
      setForm({
        feature_card_id: existingFeature._id,
        title: existingFeature.title || '',
        tab: existingFeature.tab || 'Ask GrowthIQ',
        description: existingFeature.description || '',
        url: existingFeature.url || '',
        tags: tagStr,
        released_date: existingFeature.released_date
          ? existingFeature.released_date.slice(0, 10)
          : '',
        image1: null,
        image2: null,
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setPreview1(null);
    setPreview2(null);
    setSubmitError('');
    setShowModal(true);
  };

  const handleImageChange = (e, slot) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    if (slot === 1) {
      setForm((f) => ({ ...f, image1: file }));
      setPreview1(url);
    } else {
      setForm((f) => ({ ...f, image2: file }));
      setPreview2(url);
    }
  };

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.tab) {
      setSubmitError('Title and Tab are required.');
      return;
    }
    setSubmitError('');
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('title', form.title.trim());
      fd.append('tab', form.tab);
      fd.append('description', form.description);
      fd.append('url', form.url.trim());

      // Tags as List[str] — append each separately
      const tagList = form.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      if (tagList.length > 0) {
        tagList.forEach((tag) => fd.append('tags', tag));
      }

      if (form.released_date) fd.append('released_date', form.released_date);
      if (form.feature_card_id) fd.append('feature_card_id', form.feature_card_id);
      if (form.image1) fd.append('image1', form.image1);
      if (form.image2) fd.append('image2', form.image2);

      await addFeatureCard(fd);
      setShowModal(false);
      fetchFeatures(activeTab, showDeleted);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(', ')
        : detail || 'Failed to save feature. Please try again.';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteFeatureCard(id);
      fetchFeatures(activeTab, showDeleted);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to delete feature.');
    }
  };

  const handleSendEmail = async (id) => {
    try {
      await sendEmails(id);
      alert('Emails sent successfully.');
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to send emails.');
    }
  };

  return (
    <div>
      {/* Hero */}
      <div className="nf-hero">
        <div className="nf-hero-glow" />
        <div className="nf-hero-inner">
          <div className="nf-hero-label"><img src="/img/login/Growth-IQ_black_logo.png" alt="icon" className="top_logo" style={{"width" : "200px"}}/></div>
          <h1 className="nf-hero-title">Product Releases &amp; Announcements</h1>
          <p className="nf-hero-sub">
            See every new feature, improvement and capability shipped to GrowthIQ.
          </p>
          {isAdmin && (
            <button className="btn btn-primary mt-3" onClick={() => openModal()}>
              + Add Feature
            </button>
          )}
        </div>
      </div>

      {/* Filter tabs */}
      <div className="nf-filter-bar">
        {TABS.map((tab) => (
          <button
            key={tab}
            className={`nf-pill${activeTab === tab ? ' active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
        {isAdmin && (
          <button
            className={`nf-pill nf-pill-deleted${showDeleted ? ' active-deleted' : ''}`}
            onClick={() => setShowDeleted((v) => !v)}
            style={{ marginLeft: 'auto' }}
          >
            {showDeleted ? 'Hide Deleted' : 'Show Deleted'}
          </button>
        )}
      </div>

      {/* Feature cards */}
      <div className="nf-main">
        {loading ? (
          <div className="nf-empty">
            <div className="spinner-border text-primary" role="status" />
          </div>
        ) : features.length === 0 ? (
          <div className="nf-empty">
            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>📋</div>
            <div style={{ fontSize: 16, fontWeight: 500, color: '#1A1F2E', marginBottom: 6 }}>
              No features found
            </div>
            {isAdmin && (
              <button className="btn btn-primary btn-sm mt-2" onClick={() => openModal()}>
                + Add First Feature
              </button>
            )}
          </div>
        ) : (
          <div className="nf-cards-grid">
            {features.map((f) => (
              <FeatureCard
                key={f._id}
                feature={f}
                isAdmin={isAdmin}
                onDelete={handleDelete}
                onSendEmail={handleSendEmail}
                onEdit={openModal}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add / Edit Feature Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" className="nf-modal">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 18, fontWeight: 600 }}>
            {isEditMode ? 'Edit Feature' : 'Add New Feature'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {submitError && (
            <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 13 }}>
              {submitError}
            </div>
          )}

          <Form.Group className="mb-3">
            <div className="nf-form-label">Title *</div>
            <Form.Control
              type="text"
              placeholder="Feature title"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            />
          </Form.Group>

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <div className="nf-form-label">Tab / Category *</div>
                <Form.Select
                  value={form.tab}
                  onChange={(e) => setForm((f) => ({ ...f, tab: e.target.value }))}
                >
                  {TABS.filter((t) => t !== 'All').map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <div className="nf-form-label">Released Date</div>
                <Form.Control
                  type="date"
                  value={form.released_date}
                  onChange={(e) => setForm((f) => ({ ...f, released_date: e.target.value }))}
                />
              </Form.Group>
            </div>
          </div>

          <Form.Group className="mb-3">
            <div className="nf-form-label">Description</div>
            <div className="nf-quill">
              <ReactQuill
                theme="snow"
                value={form.description}
                onChange={(val) => setForm((f) => ({ ...f, description: val }))}
                modules={QUILL_MODULES}
                placeholder="Describe this feature…"
              />
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <div className="nf-form-label">URL</div>
            <Form.Control
              type="url"
              placeholder="https://growthiq.mnmks.com/"
              value={form.url}
              onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <div className="nf-form-label">Tags (comma-separated)</div>
            <Form.Control
              type="text"
              placeholder="Latest Release, New Feature"
              value={form.tags}
              onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
            />
          </Form.Group>

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <div className="nf-form-label">
                  Image 1{isEditMode ? ' (leave blank to keep existing)' : ''}
                </div>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageChange(e, 1)}
                />
                {preview1 && (
                  <img src={preview1} alt="preview 1" className="nf-image-preview" />
                )}
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <div className="nf-form-label">
                  Image 2{isEditMode ? ' (leave blank to keep existing)' : ' (optional)'}
                </div>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageChange(e, 2)}
                />
                {preview2 && (
                  <img src={preview2} alt="preview 2" className="nf-image-preview" />
                )}
              </Form.Group>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving…' : isEditMode ? 'Update Feature' : 'Publish Feature'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
